import React from "react";
import { formatRupiah } from "../utils/FormattedPrice";
import { getProductBarcode, Product, ProductGroup } from "../types/product";

interface GroupedDetailProductProps {
  groups: ProductGroup[];
  isLoading: boolean;
  onAddUpc: (item: Product) => void;
  onSelectSerialNumber: (item: Product) => void;
  onGenerateBarcode: (product: Product) => void;
  onRemoveChild: (itemNumber: string, childIndex: number) => void;
  onUpdateChild: (
    itemNumber: string,
    childIndex: number,
    field: string,
    value: any,
  ) => void;
}

const GroupedDetailProduct: React.FC<GroupedDetailProductProps> = ({
  groups,
  isLoading,
  onAddUpc,
  onSelectSerialNumber,
  onGenerateBarcode,
  onRemoveChild,
  onUpdateChild,
}) => (
  <div className="relative overflow-x-auto">
    <table className="w-full text-sm text-left rtl:text-right text-gray-500">
      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
        <tr>
          <th className="px-6 py-3 max-w-[100px]">Product Code</th>
          <th className="px-6 py-3 max-w-[200px]">Product Name</th>
          <th className="px-6 py-3">Price</th>
          <th className="px-6 py-3">Barcode Number</th>
          <th className="px-6 py-3">Print Quantity</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {groups.map(({ item, children }) => (
          <React.Fragment key={item.itemNumber}>
            <tr className="border-b bg-gray-100">
              <td colSpan={3} className="px-6 py-4 font-semibold text-gray-900">
                {item.itemName} ({item.itemNumber})
              </td>
              <td colSpan={3} className="px-6 py-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onAddUpc(item)}
                    disabled={isLoading || children.some((child) => !child.serialNumber)}
                    className="rounded bg-gray-700 px-3 py-2 text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    UPC
                  </button>
                  {item.hasSerialNumber && (
                    <button
                      type="button"
                      onClick={() => onSelectSerialNumber(item)}
                      disabled={isLoading}
                      className="rounded bg-blue-500 px-3 py-2 text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      Pilih Serial Number
                    </button>
                  )}
                </div>
              </td>
            </tr>
            {children.map((child, childIndex) => (
              <tr
                className="border-b bg-white"
                key={`${item.itemNumber}-${child.serialNumber ?? "upc"}`}
              >
                <td className="max-w-[100px] truncate px-6 py-4 font-medium text-gray-900">
                  {child.itemNumber}
                </td>
                <td className="max-w-[200px] truncate px-6 py-4 font-medium text-gray-900">
                  {child.itemName}
                </td>
                <td className="px-6 py-4">{formatRupiah(child.itemPrice)}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-4">
                    {getProductBarcode(child) ? (
                      <span>{getProductBarcode(child)}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onGenerateBarcode(child)}
                        disabled={isLoading}
                        className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                      >
                        {isLoading ? "Generating..." : "Generate Barcode"}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="text"
                    name="printQty"
                    value={child.printQty || ""}
                    placeholder="Print Quantity"
                    className="w-full rounded border p-2"
                    pattern="[0-9]*"
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === "" || /^\d+$/.test(value)) {
                        onUpdateChild(
                          item.itemNumber,
                          childIndex,
                          "printQty",
                          value === "" ? undefined : Number(value),
                        );
                      }
                    }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() => onRemoveChild(item.itemNumber, childIndex)}
                    disabled={isLoading}
                    className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

export default GroupedDetailProduct;
